import React, { useState, useMemo } from 'react';
import { useFetchSubscriptionPlansQuery } from '../service';
import LoadingIndicator from '../../components/shared/LoadingIndicator/LoadingIndicator';
import { SubscriptionPlans as SubscriptionPlansType, SubscriptionState, UserSubscription } from '../types';
import { Button, Card, Col, List, Row } from 'antd';
import InvoiceHistory from '../InvoiceHistory';
import SubscriptionPlansHeader from './SubscriptionPlansHeader';
import { getPlanSettings } from '../utils';
import useUpdateSubscription from '../hooks/useUpdateSubscription';
import CancellationModal from '../CancellationModal';
import useCancelSubscription from '../hooks/useCancelSubscription';
import { useNavigate } from "react-router-dom";

type Props = {
    userSubscription?: UserSubscription;
    subscriptionState?: SubscriptionState;
    selectedPlanCode: string;
    isSubscriptionFreeTrial?: boolean;
    includeHeader?: boolean;
    isInFreeTrialSetup?: boolean;
    onSelectSubscription?: (planCode: string) => void;
};

export default function SubscriptionPlans({
    userSubscription,
    subscriptionState = 'none',
    selectedPlanCode,
    isSubscriptionFreeTrial,
    includeHeader,
    isInFreeTrialSetup,
    onSelectSubscription = () => { },
}: Props) {
    const navigate = useNavigate();
    const [selectedIntervalUnit, setSelectedIntervalUnit] = useState<keyof SubscriptionPlansType>(selectedPlanCode.includes('monthly') ? 'monthly' : 'yearly');
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const { data: plans, isLoading: isLoadingPlans } = useFetchSubscriptionPlansQuery();
    const { updateSubscription, updatedPlanCode, isUpdatingSubscription } = useUpdateSubscription();
    const { cancelSubscription, isCancellingSubscription } = useCancelSubscription(!userSubscription);

    const handlePlanClicked = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const plan = event.currentTarget.getAttribute('data-plan-code');
        const action = event.currentTarget.getAttribute('data-plan-action');

        if (!plan) {
            return;
        }

        if (action === 'select') {
            onSelectSubscription(plan);
        }
        if (action === 'cancel') {
            setIsModalVisible(true);
        }

        if (action === 'update') {
            updateSubscription(plan);
        }

        if (action === 'checkout') {
            navigate(`/subscription/checkout/${plan}`);
        }
    };

    const activePlans = useMemo(
        () => plans
            ? {
                monthly: plans.monthly.filter((p) => !p.settings.legacy),
                yearly: plans.yearly.filter((p) => !p.settings.legacy),
            }
            : null,
        [plans]
    );

    const yearlyPriceByName = useMemo(
        () => activePlans ? new Map(activePlans.yearly.map((p) => [p.name, p.price])) : new Map<string, number>(),
        [activePlans]
    );

    const monthlyPlansWithYearlyPrice = useMemo(
        () => activePlans?.monthly
            .map((plan) => ({ ...plan, yearlyPrice: yearlyPriceByName.get(plan.name) }))
            .sort((a, b) => a.price - b.price) ?? [],
        [activePlans, yearlyPriceByName]
    );

    const yearlyPlans = useMemo(
        () => [...(activePlans?.yearly ?? [])].sort((a, b) => a.price - b.price),
        [activePlans]
    );

    const displayPlans = selectedIntervalUnit === 'monthly' ? monthlyPlansWithYearlyPrice : yearlyPlans;


    return (
        <>
            {isLoadingPlans && <LoadingIndicator />}

            {!isLoadingPlans && plans && (
                <>
                    {isModalVisible && (
                        <CancellationModal
                            title="Confirm subscription cancellation"
                            showInfo={true}
                            showFreeTrialInfo={isSubscriptionFreeTrial}
                            isLoading={isCancellingSubscription}
                            onConfirmCancellation={cancelSubscription}
                            onClose={() => setIsModalVisible(false)}
                        />
                    )}

                    <Row
                        gutter={16}
                        className="subscription-plans"
                    >
                        <Col span={isInFreeTrialSetup ? 24 : 18}>
                            <div className="plans-container">
                                <Card
                                    className="plans-card"
                                    title={isInFreeTrialSetup ? '' : 'Select your plan'}
                                    bordered={isInFreeTrialSetup ? false : true}
                                >
                                    <Row className="plan-switch-container">
                                        <Col
                                            span={8}
                                            offset={8}
                                        >
                                            <Button.Group
                                                className="plan-switch-group"
                                                size="large"
                                            >
                                                <Button
                                                    className={`plan-switch ${selectedIntervalUnit === 'monthly' ? 'selected' : ''}`}
                                                    data-interval-unit="monthly"
                                                    onClick={() => setSelectedIntervalUnit('monthly')}
                                                >
                                                    Monthly
                                                </Button>
                                                <Button
                                                    className={`plan-switch ${selectedIntervalUnit === 'yearly' ? 'selected' : ''}`}
                                                    data-interval-unit="yearly"
                                                    onClick={() => setSelectedIntervalUnit('yearly')}
                                                >
                                                    Yearly
                                                </Button>
                                            </Button.Group>
                                        </Col>
                                    </Row>

                                    <Row
                                        className="packages flexbox custom-scrollbar"
                                        gutter={16}
                                    >
                                        {displayPlans.map(
                                            (plan) => (
                                                <Col
                                                    className="gutter-row plan-items"
                                                    key={plan.code}
                                                    span={8}
                                                >
                                                    <List
                                                        className="plan-item-list"
                                                        size="large"
                                                        bordered={false}
                                                        dataSource={getPlanSettings(plan)}
                                                        renderItem={(item) => (
                                                            <PlanSettingsItem
                                                                itemLabel={item.name}
                                                                itemValue={item.value}
                                                                cl={item.cl}
                                                            />
                                                        )}
                                                        header={
                                                            <PlanSettingsHeader
                                                                name={plan.name}
                                                                price={plan.price}
                                                                billingPeriod={plan.billing_period}
                                                                yearlyPrice={plan.yearlyPrice}
                                                            />
                                                        }
                                                        footer={
                                                            <PlanSettingsFooter
                                                                planCode={plan.code}
                                                                isLoading={isUpdatingSubscription && updatedPlanCode === plan.code}
                                                                subscriptionState={subscriptionState}
                                                                isThisPlanSelected={selectedPlanCode === plan.code}
                                                                isInFreeTrialSetup={isInFreeTrialSetup}
                                                                onPlanClicked={handlePlanClicked}
                                                            />
                                                        }
                                                    />
                                                </Col>
                                            )
                                        )}
                                    </Row>
                                </Card>
                            </div>
                        </Col>

                        {!isInFreeTrialSetup && (
                            <Col span={6}>
                                <div className="pricing-meta">
                                    {includeHeader && userSubscription && (
                                        <SubscriptionPlansHeader
                                            subscriptionStatus={userSubscription.status}
                                            planName={userSubscription.plan_details.name}
                                            planPrice={userSubscription.plan_details.price}
                                            planIntervalUnit={userSubscription.plan_details.interval_unit}
                                            planIntervalLength={userSubscription.plan_details.interval_length}
                                            currentPeriodEnd={userSubscription.current_period_ends_at}
                                            isInFreeTrial={isSubscriptionFreeTrial}
                                            includeRestartNote={subscriptionState === 'cancelled'}
                                        />
                                    )}

                                    <InvoiceHistory />
                                </div>
                            </Col>
                        )}
                    </Row>
                </>
            )}
        </>
    );
}

type PlanSettingsFooterProps = {
    planCode: string;
    subscriptionState: SubscriptionState;
    isLoading?: boolean;
    isThisPlanSelected?: boolean;
    isInFreeTrialSetup?: boolean;
    onPlanClicked: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
};

function PlanSettingsFooter({ planCode, isThisPlanSelected, subscriptionState, isInFreeTrialSetup, isLoading, onPlanClicked }: PlanSettingsFooterProps) {
    // A customer who has a subscription never pays here and is never asked for a card: the card the
    // provider already holds is the one that pays for it, so every button is a plan change. Only a
    // customer with nothing is sent to the payment details, and that is the one purchase we make.
    let planAction = 'checkout';
    let buttonLabel = 'Subscribe';

    if (subscriptionState === 'cancelled') {
        // Choosing any plan restarts the subscription; picking a different one also moves it.
        planAction = 'update';
        buttonLabel = isThisPlanSelected ? 'Reactivate subscription' : 'Change plan';
    } else if (subscriptionState === 'active') {
        planAction = isThisPlanSelected ? 'cancel' : 'update';
        buttonLabel = isThisPlanSelected ? 'Cancel subscription' : 'Change plan';
    }

    return (
        <>
            {isInFreeTrialSetup && (
                <Button
                    className={isThisPlanSelected ? 'disabled' : ''}
                    type="primary"
                    block
                    onClick={onPlanClicked}
                    data-plan-code={planCode}
                    data-plan-action="select"
                >
                    {isThisPlanSelected ? 'Selected plan' : 'Free trial'}
                </Button>
            )}
            {!isInFreeTrialSetup && (
                <Button
                    className={planAction === 'cancel' ? 'cancel' : ''}
                    type="primary"
                    block
                    onClick={onPlanClicked}
                    loading={isLoading}
                    data-plan-code={planCode}
                    data-plan-action={planAction}
                >
                    {buttonLabel}
                </Button>
            )}
        </>
    );
}

type PlanSettingsHeaderProps = {
    name: string;
    price: number;
    billingPeriod: string;
    yearlyPrice?: number;
};

function PlanSettingsHeader({ name, price, billingPeriod, yearlyPrice }: PlanSettingsHeaderProps) {
    const getPlanPrice = () => {
        if (billingPeriod === 'yearly') {
            return (price / 12 / 100).toFixed(2);
        }

        return (price / 100).toFixed(2);
    };

    const getPlanDiscountMessage = () => {
        if (billingPeriod === 'yearly') {
            return `billed yearly, $${price / 100}/year`;
        }
        return yearlyPrice ? `$${(yearlyPrice / 1200).toFixed(2)}/mo billed yearly` : '';
    };

    return (
        <div className="plan-header">
            <div className="plan-name">{name}</div>
            <div className="plan-price">
                <sup>$</sup>
                <span className="value">{getPlanPrice()}</span>
                <span>/mo</span>
            </div>
            <div className="plan-discount-message">{getPlanDiscountMessage()}</div>
        </div>
    );
}

function PlanSettingsItem({ itemLabel, itemValue, cl }: { itemLabel: string; itemValue: string | number | boolean, cl: string }) {
    return (
        // <List.Item>
        //     <div className="pricing-item">
        //         <div className="label">{itemLabel}</div>
        //         {itemLabel.toLowerCase() === 'white label reports' ? (
        //             <div className={`value ${(itemValue as string).toLowerCase() === 'included' ? 'included' : 'not-included'}`}>{itemValue}</div>
        //         ) : itemLabel.toLowerCase() === 'team members' && itemValue === 'Not included' ? (
        //             <div className="value not-included">{itemValue}</div>
        //         ) : (
        //             <div className="value">{itemValue}</div>
        //         )}
        //     </div>
        // </List.Item>

        <List.Item>
            <div className="pricing-item">
                <div className="label">{itemLabel}</div>
                <div className={cl}>{itemValue}</div>
            </div>
        </List.Item>
    );
}
